import { useEffect, useMemo, useState } from "react";
import { api, ApiError } from "./api";
import { purchaseName, refundStatus } from "./trust";

/** The owner's feedback inbox (/admin): every Zo app's anonymous notes, newest first. Preview hosts only (Access). */

export interface FeedbackRow { app: string; text: string; page: string; version: string; created_at: number; reply_to?: string | null }
const SEEN_KEY = "loom:admin-seen";
const seenAt = () => { try { return Number(localStorage.getItem(SEEN_KEY) ?? 0); } catch { return 0; } };
const NAMES: Record<string, string> = { loom: "Weftwake", threads: "Threads", soulfile: "Soulfile", "sleep-on-it": "Sleep On It", "main-character": "Main Character" };

function ago(t: number): string {
  const m = Math.round((Date.now() - t) / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h} h ago`;
  return new Date(t).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

const money = (cents: number) => `$${(cents / 100).toFixed(2)}`;

export function Admin({ goHome }: { goHome(): void }) {
  const [rows, setRows] = useState<FeedbackRow[] | null>(null);
  const [error, setError] = useState<"hidden" | "failed" | null>(null);
  const [app, setApp] = useState<string>("all");
  const [since] = useState(seenAt);
  const load = () => {
    setError(null);
    api.adminFeedback().then((r) => {
      setRows(r.feedback);
      try { localStorage.setItem(SEEN_KEY, String(Date.now())); } catch { /* only marks what's new */ }
    }, (e) => setError(e instanceof ApiError && e.status === 404 ? "hidden" : "failed"));
  };
  useEffect(load, []);
  const [refunds, setRefunds] = useState<Awaited<ReturnType<typeof api.adminRefunds>>["withdrawals"] | null>(null);
  useEffect(() => { api.adminRefunds().then((r) => setRefunds(r.withdrawals), () => setRefunds([])); }, []);
  const owed = (refunds ?? []).filter((w) => w.refunded < w.refund_due);
  // The Overview: who's playing, who signed up, who pays, and today's AI spend (refreshes every minute while open).
  const [ov, setOv] = useState<Awaited<ReturnType<typeof api.adminOverview>> | null>(null);
  useEffect(() => {
    const get = () => { api.adminOverview().then(setOv, () => {}); };
    get();
    const t = window.setInterval(get, 60_000);
    return () => window.clearInterval(t);
  }, []);
  // Friend passes: an email plays as a Founding Patron, free, until ended here.
  const [passes, setPasses] = useState<Awaited<ReturnType<typeof api.adminPasses>>["passes"] | null>(null);
  const [passEmail, setPassEmail] = useState("");
  const [passError, setPassError] = useState<string | null>(null);
  const loadPasses = () => { api.adminPasses().then((r) => setPasses(r.passes), () => setPasses([])); };
  useEffect(loadPasses, []);
  const addPass = async () => {
    setPassError(null);
    try { await api.setPass(passEmail.trim()); setPassEmail(""); loadPasses(); } catch { setPassError("That doesn't look like an email address."); }
  };
  const endPass = (email: string) => { void api.setPass(email, true).then(loadPasses, loadPasses); };
  // Openings players passed on and someone reported: hidden at the report until restored or deleted here.
  const [seeds, setSeeds] = useState<Awaited<ReturnType<typeof api.adminSeeds>>["seeds"] | null>(null);
  const loadSeeds = () => { api.adminSeeds().then((r) => setSeeds(r.seeds), () => setSeeds([])); };
  useEffect(loadSeeds, []);
  const settle = (code: string, action: "restore" | "delete") => { void api.settleSeed(code, action).then(loadSeeds, loadSeeds); };
  const apps = useMemo(() => [...new Set((rows ?? []).map((r) => r.app))], [rows]);
  const shown = (rows ?? []).filter((r) => app === "all" || r.app === app);
  const fresh = (rows ?? []).filter((r) => r.created_at > since).length;
  return (
    <main className="lm-page lm-admin">
      <a href="/" onClick={(e) => { e.preventDefault(); goHome(); }}>← back to Weftwake</a>
      <h1>Overview</h1>
      {!ov ? <p aria-live="polite">Loading…</p> : (
        <>
          <dl className="lm-admin-stats">
            <div><dt>Playing</dt><dd>{ov.players.day}</dd><span>in the last 24 h · {ov.players.week} this week</span></div>
            <div><dt>Stories played</dt><dd>{ov.players.storiesPlayed}</dd><span>all time</span></div>
            <div><dt>Accounts</dt><dd>{ov.accounts.total}</dd><span>{ov.accounts.day} new today · {ov.accounts.week} this week</span></div>
            <div><dt>Patrons</dt><dd>{ov.patrons.total}</dd><span>{Object.entries(ov.patrons.byPlan).map(([k, v]) => `${v} ${k}`).join(" · ") || "none yet"}{ov.patrons.passes ? ` · ${ov.patrons.passes} friend passes` : ""}</span></div>
            <div><dt>Founding seats left</dt><dd>{ov.patrons.foundingLeft}</dd><span>of 200</span></div>
            <div><dt>Paid</dt><dd>{money(ov.money.dayCents)}</dd><span>today · {money(ov.money.weekCents)} this week · {money(ov.money.totalCents - ov.money.refundedCents)} all time, after refunds</span></div>
            <div><dt>AI today</dt><dd>${ov.spend.spentUsd.toFixed(2)}</dd><span>of ${ov.spend.capUsd.toFixed(0)}{(ov.spend.paidCapUsd ?? 0) > ov.spend.capUsd ? ` free play, $${ov.spend.paidCapUsd!.toFixed(0)} for Patrons` : ""} · {Math.round((ov.spend.spentUsd / Math.max(1, ov.spend.capUsd)) * 100)}% (UTC day)</span></div>
          </dl>
          <h2 className="lm-admin-h2">Patrons ({ov.patronList.length})</h2>
          {!ov.patronList.length ? <p>No Patrons yet.</p> : (
            <ul className="lm-withdraw">
              {ov.patronList.map((x, i) => (
                <li key={i}><span><strong>{x.plan}</strong> · {x.email ?? "no email"} · since {x.started_at ? new Date(x.started_at).toLocaleDateString() : "?"}{x.period_end ? ` · ${x.cancel_at_end ? "ends" : "renews"} ${new Date(x.period_end).toLocaleDateString()}` : ""}{x.status !== "active" ? ` · ${x.status}` : ""}</span></li>
              ))}
            </ul>
          )}
          <h2 className="lm-admin-h2">Newest accounts</h2>
          {!ov.newestAccounts.length ? <p>No accounts yet.</p> : (
            <ul className="lm-withdraw">
              {ov.newestAccounts.map((x) => (
                <li key={x.email}><span>{x.email} · joined {new Date(x.created_at).toLocaleString()} · last sign-in {new Date(x.signed_in_at).toLocaleString()}{x.patron ? <> · <strong>{x.patron}</strong></> : null}</span></li>
              ))}
            </ul>
          )}
          <h2 className="lm-admin-h2">Where players come from (30 days)</h2>
          {!ov.referrals.length ? <p>No visits counted yet.</p> : (
            <ul className="lm-withdraw">
              {ov.referrals.map((x) => (
                <li key={x.source}><span><strong>{x.source}</strong> · {x.visits} first visit{x.visits === 1 ? "" : "s"} · {x.stories} first stor{x.stories === 1 ? "y" : "ies"}</span></li>
              ))}
            </ul>
          )}
          <p className="lm-gate-small">A source is a link's ?ref= tag (taaft, itch) or the site a visitor came from; "direct" is neither. First visits count people new to weftwake.com; first stories, players beginning their first story. Counts only, never linked to anyone.</p>
          <p className="lm-gate-small">Players without an account aren't listed by name: they play anonymously. Refreshes every minute.</p>
        </>
      )}
      <h1>Refunds</h1>
      {!refunds ? <p aria-live="polite">Loading refunds…</p> : !refunds.length ? <p>No withdrawals yet.</p> : (
        <>
          <p>{owed.length ? `${owed.length} refund${owed.length === 1 ? "" : "s"} still owed: refund each in the Stripe dashboard; it's marked paid when Stripe reports it.` : "Every withdrawal is refunded."}</p>
          <ul className="lm-withdraw">
            {refunds.map((w) => <li key={w.id}><span><strong>{w.refunded < w.refund_due ? "OWED" : "paid"}</strong> · {w.email ?? "unknown"} · {purchaseName(w.item)} · withdrawn {new Date(w.withdrawn_at).toLocaleDateString()} · {refundStatus(w)} · {w.id}</span></li>)}
          </ul>
        </>
      )}
      <h1>Friend passes</h1>
      <p>Each email plays as a Founding Patron (600 turns a month, a dream and a plate every night), free, once it signs in at weftwake.com with that address. It lasts until you end it, and doesn't take a founding seat.</p>
      <form className="lm-admin-pass" onSubmit={(e) => { e.preventDefault(); void addPass(); }}>
        <label htmlFor="lm-pass-email" className="sr">Email for a friend pass</label>
        <input id="lm-pass-email" type="email" value={passEmail} onChange={(e) => setPassEmail(e.target.value)} placeholder="friend@example.com" autoComplete="off" />
        <button type="submit" disabled={!passEmail.trim()}>Give a pass</button>
      </form>
      {passError && <p role="alert">{passError}</p>}
      {!passes ? <p aria-live="polite">Loading…</p> : !passes.length ? <p>No friend passes yet.</p> : (
        <ul className="lm-withdraw">
          {passes.map((x) => (
            <li key={x.email}>
              <span><strong>{x.ended_at ? "ended" : "active"}</strong> · {x.email} · given {new Date(x.created_at).toLocaleDateString()} · {x.signed_in_at ? `last signed in ${new Date(x.signed_in_at).toLocaleDateString()}` : "hasn't signed in yet"}</span>
              {" "}{x.ended_at
                ? <button type="button" onClick={() => { void api.setPass(x.email).then(loadPasses, loadPasses); }}>Give again</button>
                : <button type="button" onClick={() => endPass(x.email)}>End pass</button>}
            </li>
          ))}
        </ul>
      )}
      <h1>Reported openings</h1>
      {!seeds ? <p aria-live="polite">Loading…</p> : !seeds.length ? <p>No opening has been reported.</p> : (
        <>
          <p>An opening a player passed on is hidden the moment someone reports it. Restore it, or delete it: the Terms (5.7) promise a decision within 24 hours. You get an email at each report.</p>
          <ul className="lm-withdraw">
            {seeds.map((x) => (
              <li key={x.code}>
                <span><strong>{x.hidden ? "HIDDEN" : "shown"}</strong> · reported {new Date(x.reportedAt).toLocaleString()}{x.mature ? " · mature" : ""} · “{x.opening}”{x.truths.length ? ` · true: ${x.truths.join(" / ")}` : ""}</span>
                {" "}<button type="button" onClick={() => settle(x.code, "restore")}>Restore</button>{" "}
                <button type="button" onClick={() => settle(x.code, "delete")}>Delete</button>
              </li>
            ))}
          </ul>
        </>
      )}
      <h1>Feedback</h1>
      {error === "hidden" && <p>The feedback inbox only opens on the preview address, behind your Cloudflare login.</p>}
      {error === "failed" && <p role="alert">Couldn't load feedback. <button type="button" onClick={load}>Try again</button></p>}
      {!rows && !error && <p aria-live="polite">Loading feedback…</p>}
      {rows && (
        <>
          <p>{rows.length === 0 ? "No feedback yet." : `${rows.length} note${rows.length === 1 ? "" : "s"}${fresh ? `, ${fresh} new since your last visit` : ""}. Anonymous: no player id or story is attached; a reply address only when the player left one.`}</p>
          {apps.length > 1 && (
            <div className="lm-admin-filter" role="radiogroup" aria-label="App">
              {["all", ...apps].map((a) => (
                <button key={a} type="button" role="radio" aria-checked={app === a} onClick={() => setApp(a)}>
                  {a === "all" ? "All" : NAMES[a] ?? a} ({a === "all" ? rows.length : rows.filter((r) => r.app === a).length})
                </button>
              ))}
            </div>
          )}
          <ol className="lm-admin-list">
            {shown.map((r, i) => (
              <li key={`${r.created_at}-${i}`} className={r.created_at > since ? "lm-admin-new" : ""}>
                <p className="lm-admin-meta">
                  {r.created_at > since && <strong>new · </strong>}
                  {NAMES[r.app] ?? r.app} · {r.page} · {ago(r.created_at)} · v {r.version.slice(0, 8)}
                </p>
                <p className="lm-admin-text">{r.text}</p>
                {r.reply_to && (
                  <p className="lm-admin-meta">
                    Wants a reply: <a href={`mailto:${r.reply_to}?subject=${encodeURIComponent("Re: your Weftwake feedback")}&body=${encodeURIComponent(`You wrote:\n\n> ${r.text.replace(/\n/g, "\n> ")}\n\n`)}`}>{r.reply_to}</a>
                  </p>
                )}
              </li>
            ))}
          </ol>
        </>
      )}
    </main>
  );
}
